import { ApplicationStatus, BaseAPIResponse, CreateApplicationInput, UIExtendedApplication } from 'types';
import ApplicationDrawerHeaderFooter from './ApplicationDrawerHeaderFooter';
import { ApplicationDrawerDetail } from 'src/pages/MyApplications';
import { UseMutationResult } from 'react-query';
import { useEffect, useRef } from 'react';
import { Drawer } from 'vaul';
import ReactQuill from 'react-quill';
import { HiOutlineBriefcase, HiOutlineClock, HiOutlineCurrencyRupee, HiOutlineExternalLink, HiOutlineLocationMarker, HiX } from 'react-icons/hi';
import {
	CompanyLogo,
	DRIVE_FONT_STYLE,
	formatPositionTitle,
	formatWorkingMode,
	PositionChip,
	useDriveFont,
} from 'src/components/placementDrives/shared';
import { useLocation } from 'react-router-dom';
import { sendTrackingEvent } from 'src/utils/gtm';

// Ongoing application statuses
const ongoingStatuses = [
	ApplicationStatus.SHORTLISTED,
	ApplicationStatus.INTERVIEW_SCHEDULED,
	ApplicationStatus.ASSIGNMENT_IN_PROGRESS,
	ApplicationStatus.CASE_STUDY_IN_PROGRESS,
	ApplicationStatus.OFFER_ROLLED_OUT,
	ApplicationStatus.WAITING_FOR_RECRUITER_ACTION,
	ApplicationStatus.QUALIFIED_ALL_ROUNDS,
	ApplicationStatus.PENDING_FOR_ASSIGNMENT_SUBMISSION_APPROVAL,
	ApplicationStatus.PENDING_FOR_CASE_STUDY_SUBMISSION_APPROVAL,
	ApplicationStatus.PENDING_FOR_OFFER_LETTER_APPROVAL,
	ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL,
	ApplicationStatus.CHANGED_OFFER_TERMS,
];

interface Props {
	isApplicationDrawerOpen: boolean;
	setIsApplicationDrawerOpen: (value: boolean) => void;
	detail: ApplicationDrawerDetail;
	showCta: boolean;
	myApplicationStatus: ApplicationStatus;
	onApplyModalOpen: () => void;
	setIsLinkedInResumeModalOpen: (value: boolean) => void;
	setCurrentlySelectedApplication: (arg: UIExtendedApplication) => void;
	createApplicationMutation: UseMutationResult<BaseAPIResponse, Error, CreateApplicationInput>;
	application: UIExtendedApplication;
}

const ApplicationDrawer = ({
	isApplicationDrawerOpen,
	setIsApplicationDrawerOpen,
	detail,
	showCta,
	onApplyModalOpen,
	myApplicationStatus,
	setIsLinkedInResumeModalOpen,
	setCurrentlySelectedApplication,
	createApplicationMutation,
	application,
}: Props) => {
	const location = useLocation();
	const hasTrackedJobDescriptionView = useRef(false);

	useEffect(() => {
		if (!isApplicationDrawerOpen) {
			hasTrackedJobDescriptionView.current = false;
			return;
		}
		if (!detail?.id || hasTrackedJobDescriptionView.current) return;
		hasTrackedJobDescriptionView.current = true;
		sendTrackingEvent({
			event: 'view_job_description',
			eventLabel: location.pathname,
			application_id: detail.id,
			company_name: detail.companyName || '',
			position_title: detail.companyPosition || '',
		});
	}, [isApplicationDrawerOpen, detail?.id, detail.companyName, detail.companyPosition, location.pathname]);

	// Check if we should show the links
	const shouldShowLinks =
		application.status === ApplicationStatus.APPLIED_BY_JOBSEEKER ||
		ongoingStatuses.includes(application.status as ApplicationStatus);

	// Get company pre-placement call recording
	const companyPrePlacementLink = application.position?.company?.prePlacementCallRecording;
	const showCompanyLink = application.position?.showPrePlacementCallRecordingToStudent;

	// Get position prep material
	const prepMaterialLinks = application.position?.prepMaterial || [];
	const showPrepMaterial = application.position?.showPrepMaterialToStudent;

	useDriveFont();
	const shortSalary = detail?.minSalary === detail?.maxSalary ? `${detail?.maxSalary}` : `${detail?.minSalary} - ${detail?.maxSalary}`;
	const isInternship = detail?.workingMode === 'Internship' || detail?.workingMode === 'InternshipToFullTime';
	const experience = /^\s*0 Yrs 0 Mon - 0 Yrs 0 Mon/.test(detail?.workingExp || '') ? 'Fresher' : (detail?.workingExp || '').trim();

	return (
		<Drawer.Root open={isApplicationDrawerOpen} onOpenChange={open => setIsApplicationDrawerOpen(open)}>
			<Drawer.Portal>
				<Drawer.Overlay className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px]" />
				<Drawer.Content
					className="fixed inset-x-0 bottom-0 z-[61] flex max-h-[92vh] w-full flex-col rounded-t-[28px] bg-white shadow-[0_-12px_48px_rgba(20,20,60,0.22)] outline-none dark:bg-[#1F2A37]"
					style={DRIVE_FONT_STYLE}
				>
					<Drawer.Title className="sr-only">{detail.companyPosition}</Drawer.Title>
					<Drawer.Description className="sr-only">Job details for {detail.companyName}</Drawer.Description>
					<div className="mx-auto mt-3 h-1.5 w-11 shrink-0 rounded-full bg-[#E3E3EC] dark:bg-[#4B5563]" />

					<div className="mx-auto flex w-full max-w-[1280px] shrink-0 items-start gap-4 px-5 pb-4 pt-5 md:px-8">
						<CompanyLogo name={detail.companyName} logo={detail.companyLogo} url={detail.companyUrl} size={60} />
						<div className="min-w-0 flex-1">
							<h2 className="text-[22px] leading-[28px] font-[800] tracking-[-0.02em] text-[#111E2C] dark:text-white">
								{formatPositionTitle(detail.companyPosition)}
							</h2>
							<button
								type="button"
								onClick={() => window.open(`${detail.companyUrl}`, '_blank')}
								className="mt-1 flex items-center gap-1.5 text-[15px] font-[500] text-[#555770] hover:text-[#111E2C] dark:text-[#C7C9D9]"
							>
								<span className="truncate">{detail.companyName}</span>
								<HiOutlineExternalLink className="h-4 w-4 shrink-0 text-[#4F46E5]" />
							</button>
						</div>
						<button
							type="button"
							aria-label="Close"
							onClick={() => setIsApplicationDrawerOpen(false)}
							className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F1F1F5] text-[#111E2C] hover:bg-[#E6E6EE] dark:bg-[#374151] dark:text-white"
						>
							<HiX className="h-[18px] w-[18px]" />
						</button>
					</div>

					<div className="flex-1 overflow-y-auto border-t border-[#EEEEF5] dark:border-[#374151]" data-vaul-no-drag>
					<div className="mx-auto w-full max-w-[1280px] px-5 pb-6 pt-5 md:px-8">
						<div className="flex flex-wrap gap-2">
							{detail?.workingMode ? (
								<PositionChip icon={HiOutlineBriefcase} text={`${formatWorkingMode(detail.workingMode)}${experience ? ` · ${experience}` : ''}`} />
							) : null}
							{isInternship && detail?.maxInternshipSalary ? (
								<PositionChip
									icon={HiOutlineCurrencyRupee}
									text={`Internship: ${
										detail.minInternshipSalary === detail.maxInternshipSalary
											? detail.maxInternshipSalary
											: `${detail.minInternshipSalary} - ${detail.maxInternshipSalary}`
									}K / month`}
								/>
							) : null}
							{detail?.minSalary || detail?.maxSalary ? <PositionChip icon={HiOutlineCurrencyRupee} text={`${shortSalary} LPA`} /> : null}
							{detail?.location || detail?.jobType ? (
								<PositionChip icon={HiOutlineLocationMarker} text={[detail.location, detail.jobType].filter(Boolean).join(' · ')} />
							) : null}
							{detail?.internshipDuration ? <PositionChip icon={HiOutlineClock} text={`${detail.internshipDuration} months`} /> : null}
						</div>

						{detail?.hasBond ? (
							<div className="mt-4 rounded-2xl bg-[#FFF8E1] px-4 py-3 text-[13px] font-[600] text-[#8A6100]">
								This job includes a bond agreement.
								{detail?.bondDetails ? <p className="mt-1 font-[400] leading-[20px]">{detail.bondDetails}</p> : null}
							</div>
						) : null}

						<h3 className="mt-7 text-[13px] font-[700] uppercase tracking-[0.06em] text-[#111E2C] dark:text-white">Job description</h3>
						<div className="quill-container position-drawer-quill-editor mt-3 text-[#28293D] dark:text-[#C6C9D9]">
							<ReactQuill modules={{ toolbar: false }} readOnly value={detail?.description} />
						</div>

						{/* Pre-Placement Resources Section */}
						{shouldShowLinks && ((companyPrePlacementLink && showCompanyLink) || (prepMaterialLinks.length > 0 && showPrepMaterial)) && (
							<div className="mt-6 rounded-2xl bg-[#F6F6FA] p-5 dark:bg-[#111E2C]">
								<h3 className="text-[15px] font-[700] text-[#111E2C] dark:text-white">Pre-placement resources</h3>

								{companyPrePlacementLink && showCompanyLink && (
									<div className="mt-3">
										<h4 className="text-[13px] font-[600] text-[#555770] dark:text-gray-300">Pre-placement call recording</h4>
										<a
											href={companyPrePlacementLink}
											target="_blank"
											rel="noopener noreferrer"
											className="break-all text-[14px] text-[#3730A3] hover:underline dark:text-[#8F89F5]"
										>
											{companyPrePlacementLink}
										</a>
									</div>
								)}

								{prepMaterialLinks.length > 0 && showPrepMaterial && (
									<div className="mt-3">
										<h4 className="text-[13px] font-[600] text-[#555770] dark:text-gray-300">Prep material</h4>
										<ul className="mt-1 space-y-2">
											{prepMaterialLinks.map((link, index) => (
												<li key={index}>
													<a
														href={link}
														target="_blank"
														rel="noopener noreferrer"
														className="break-all text-[14px] text-[#3730A3] hover:underline dark:text-[#8F89F5]"
													>
														{link}
													</a>
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						)}
					</div>
					</div>

					<div
						style={{ display: showCta ? '' : 'none' }}
						className="shrink-0 border-t border-[#EEEEF5] bg-white pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 dark:border-[#374151] dark:bg-[#1F2A37]"
					>
						<div className="mx-auto w-full max-w-[1280px] px-5 md:px-8">
						<ApplicationDrawerHeaderFooter
							company={detail}
							setIsLinkedInResumeModalOpen={setIsLinkedInResumeModalOpen}
							isLoadingApplying={detail.isLoadingApplying}
							isFooter={true}
							showCta={showCta}
							myApplicationStatus={myApplicationStatus}
							setIsApplicationDrawerOpen={setIsApplicationDrawerOpen}
							setCurrentlySelectedApplication={setCurrentlySelectedApplication}
							createApplicationMutation={createApplicationMutation}
							application={application}
						/>
						</div>
					</div>
				</Drawer.Content>
			</Drawer.Portal>
		</Drawer.Root>
	);
};

export default ApplicationDrawer;
